/* client/src/contexts/MaterialsContext.tsx */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiGetMaterials, apiAddMaterial, apiUpdateMaterial, apiDeleteMaterial } from '../services/apiService';

interface Material {
  id: number;
  name: string;
  unit_price: number;
  unit: string;
  description?: string;
  type_id?: number;
  type_name?: string;
  type_unit?: string;
  is_active: boolean;
  created_at: string;
}

interface MaterialsContextType {
  materials: Material[];
  loading: boolean;
  error: string;
  refreshMaterials: () => Promise<void>;
  addMaterial: (materialData: any) => Promise<Material>;
  updateMaterial: (id: number, materialData: any) => Promise<void>;
  deleteMaterial: (id: number) => Promise<void>;
  getMaterialById: (id: number) => Material | undefined;
}

const MaterialsContext = createContext<MaterialsContextType | undefined>(undefined);

export const useMaterials = () => {
  const context = useContext(MaterialsContext);
  if (!context) {
    throw new Error('useMaterials must be used within a MaterialsProvider');
  }
  return context;
};

interface MaterialsProviderProps {
  children: ReactNode;
}

export const MaterialsProvider: React.FC<MaterialsProviderProps> = ({ children }) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiGetMaterials();
      setMaterials(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch materials');
      console.error('Materials fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshMaterials = async () => {
    await fetchMaterials();
  };

  const addMaterial = async (materialData: any): Promise<Material> => {
    try {
      setError('');
      const newMaterial = await apiAddMaterial(materialData);
      await fetchMaterials(); // Refresh the list
      return newMaterial;
    } catch (err: any) {
      setError(err.message || 'Failed to add material');
      throw err;
    }
  };

  const updateMaterial = async (id: number, materialData: any): Promise<void> => {
    try {
      setError('');
      await apiUpdateMaterial(id, materialData);
      await fetchMaterials(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to update material');
      throw err;
    }
  };

  const deleteMaterial = async (id: number): Promise<void> => {
    try {
      setError('');
      await apiDeleteMaterial(id);
      await fetchMaterials(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete material');
      throw err;
    }
  };

  const getMaterialById = (id: number): Material | undefined => {
    return materials.find(material => material.id === id);
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  const value: MaterialsContextType = {
    materials,
    loading,
    error,
    refreshMaterials,
    addMaterial,
    updateMaterial,
    deleteMaterial,
    getMaterialById
  };

  return (
    <MaterialsContext.Provider value={value}>
      {children}
    </MaterialsContext.Provider>
  );
};

export default MaterialsContext;